import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { fetchInventory, fetchStores } from '../api/api';
import { computeCartTotals, getAvailableQuantity as computeAvailableQuantity } from '../utils/cartMath';

const CART_STORAGE_KEY = 'zimzo-cart';

const CartContext = createContext();

export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [inventoryMap, setInventoryMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [stores, setStores] = useState([]);

  // Needed for computeCartTotals' per-store minimumOrderValue check below -
  // fetched once here rather than separately in CartModal/Checkout, which
  // both used to fetch the exact same store list themselves just for names.
  useEffect(() => {
    fetchStores()
      .then(setStores)
      .catch((err) => console.error('Failed to fetch stores', err));
  }, []);

  const storeMinimums = useMemo(
    () => Object.fromEntries(stores.map((s) => [s.id, s.minimumOrderValue || 0])),
    [stores]
  );

  // Load cart from localStorage on init
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (err) {
        console.error('Error loading cart from localStorage', err);
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  // Refresh inventory for every store already represented in the cart, so
  // multi-store carts keep accurate stock for items already added. Merges
  // into the existing map rather than replacing it, since a store the
  // customer is currently browsing (see refreshStoreInventory below) may
  // have already populated entries this call doesn't cover.
  const refreshInventory = () => {
    const now = Date.now();
    if (now - lastRefreshTime < 5000 && lastRefreshTime !== 0) {
      return Promise.resolve();
    }

    setLastRefreshTime(now);
    const storeIds = [...new Set(cartItems.map((item) => item.storeId).filter(Boolean))];
    if (storeIds.length === 0) return Promise.resolve();

    setLoading(true);
    return Promise.all(storeIds.map((storeId) => fetchInventory(null, storeId)))
      .then((results) => {
        setInventoryMap((prev) => Object.assign({}, prev, ...results));
      })
      .catch((error) => {
        console.error('Failed to fetch inventory data', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Called by the store-menu page whenever a customer views a store, so
  // that store's stock is available for add-to-cart checks even before
  // anything from it is in the cart (refreshInventory above only covers
  // stores already in the cart, which is too late for the "can I add this"
  // check on first visit).
  const refreshStoreInventory = (storeId) => {
    if (!storeId) return Promise.resolve();
    return fetchInventory(null, storeId)
      .then((data) => {
        setInventoryMap((prev) => ({ ...prev, ...data }));
      })
      .catch((error) => {
        console.error(`Failed to fetch inventory for store ${storeId}`, error);
      });
  };

  const getAvailableQuantity = (productId) => computeAvailableQuantity(inventoryMap, cartItems, productId);

  const addToCart = (product) => {
    const productId = product.productId || product.id;
    const available = computeAvailableQuantity(inventoryMap, cartItems, productId);

    let adjustedQuantity = product.quantity;
    if (adjustedQuantity > available) {
      if (available <= 0) {
        return { ok: false, reason: 'out-of-stock' };
      }
      adjustedQuantity = available;
    }

    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex((item) => {
        const itemId = item.productId || item.id;
        const productToAddId = product.productId || product.id;
        const itemCustomizations = JSON.stringify(item.customizations || []);
        const productCustomizations = JSON.stringify(product.customizations || []);
        return itemId === productToAddId && itemCustomizations === productCustomizations;
      });

      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        const currentStock = inventoryMap?.[productId]?.currentStock || 0;
        const newQuantity = updatedItems[existingItemIndex].quantity + adjustedQuantity;
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: Math.min(newQuantity, currentStock)
        };
        return updatedItems;
      }

      return [
        ...prevItems,
        { ...product, quantity: adjustedQuantity, customizations: product.customizations || [] }
      ];
    });

    return { ok: true, adjusted: adjustedQuantity !== product.quantity, quantity: adjustedQuantity };
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;

    const item = cartItems[index];
    if (!item) return;

    const productId = item.productId || item.id;
    const currentStock = inventoryMap?.[productId]?.currentStock || 0;
    const otherSameItemsInCart = cartItems.reduce((total, cartItem, idx) => {
      const itemProductId = cartItem.productId || cartItem.id;
      return itemProductId === productId && idx !== index ? total + cartItem.quantity : total;
    }, 0);

    const maxAvailable = Math.max(0, currentStock - otherSameItemsInCart);
    const adjustedQuantity = Math.min(newQuantity, maxAvailable);

    setCartItems((prevItems) => {
      const updatedItems = [...prevItems];
      updatedItems[index] = { ...updatedItems[index], quantity: adjustedQuantity };
      return updatedItems;
    });

    return adjustedQuantity;
  };

  const removeItem = (index) => {
    setCartItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const updateDeliveryMethod = (method) => {
    setDeliveryMethod(method);
  };

  const totals = useMemo(
    () => computeCartTotals(cartItems, deliveryMethod, storeMinimums),
    [cartItems, deliveryMethod, storeMinimums]
  );

  const getCartTotal = () => ({
    subtotal: totals.subtotal,
    packagingCharge: totals.packagingCharge,
    deliveryFee: totals.deliveryFee,
    total: totals.total,
    itemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0)
  });

  const value = {
    cartItems,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    getCartTotal,
    storeGroups: totals.groups,
    subtotal: totals.subtotal,
    packagingCharge: totals.packagingCharge,
    deliveryFee: totals.deliveryFee,
    total: totals.total,
    getAvailableQuantity,
    refreshInventory,
    refreshStoreInventory,
    inventoryLoading: loading,
    inventoryMap,
    deliveryMethod,
    updateDeliveryMethod,
    stores
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
